import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import addDays from 'date-fns/addDays';
import cn from 'classnames';
import { loadSavedListDetails } from '@app/store/account/actions';
import { useBreakpoint } from '@app/hooks';
import usePagination from '@app/hooks/usePagination';
import useListSorting from '@app/hooks/useListSorting';
import useManageView from '@app/components/common/modals/ManageViewModal/hooks/useManageView';
import { CatPagination } from '@blocks-react/components/Pagination';
import SavedListTable from '../SavedListTable/SavedListTable';
import {
  AFFILIATION_DEALER,
  PAGE_BREAKPOINTS,
  PAGE_SIZE_OPTIONS,
  SORT_DIRECTIONS,
  SORT_TYPE,
  STATUS
} from '@app/constants/commonConstants';
import {
  FIRST_PAGE,
  PAGE_LIMIT,
  SAVED_LIST_COLUMN_IDS,
  SAVED_LIST,
  SAVED_LIST_MANAGE_VIEW_SCHEMA,
  SAVED_LIST_MANAGE_VIEW_FIELDS
} from '../constants';
import { CatToggle } from '@blocks-react/components/Toggle';
import ManageView from '../common/ManageView';
import LargePaginationLoader from '@app/components/common/Blocks/Loader/LargePaginationLoader';
import SavedListTableEmptyState from '../SavedListTable/SavedListTableEmptyState';
import 'blocks-web-components/dist/bedrock/bedrock.css';
import styles from './savedListTableContainer.module.scss';

const SavedListTableContainer = ({ openCreateListModal }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const [pageSize, setPageSize] = useState(PAGE_LIMIT);
  const savedLists = useSelector(s => s.account?.savedLists) || [];
  const savedListsStatus = useSelector(s => s.account?.savedListsStatus);
  const resetSavedListPagination = useSelector(
    s => s.account?.resetSavedListPagination
  );
  const listPreferenceLoading = useSelector(
    s => s.account?.listPreferenceLoading
  );
  const isCSPCustomer = useSelector(s => s.common?.isCSPCustomer);
  const isInstantAccess = useSelector(s => s.common?.isInstantAccess);
  const loadingStatus = useSelector(
    s => s.user?.byId?.[s.user.current].loadingStatus
  );
  const isLoading = useSelector(s => s.account?.isLoading);
  const searchOptions = useSelector(s => s.account.searchOptions);
  const userAffiliation = useSelector(s => s.common?.userAffiliation);
  const isCatCSR = useSelector(s => s.common?.isCatCSR) || false;
  const isDealerUser = userAffiliation === AFFILIATION_DEALER;
  const showFavToggle = (isInstantAccess && !isCSPCustomer) || isDealerUser;
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const firstRender = useRef(true);
  const { saveManageViewPreferences, getManageViewPreferences, getValues } =
    useManageView({
      uniquePrefix: SAVED_LIST,
      columns: SAVED_LIST_MANAGE_VIEW_SCHEMA
    });
  const { initialValues } = getValues();
  const showMyLists =
    initialValues[SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_LISTS];
  const showMyFavs = initialValues[SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_FAVS];
  const initialSortData = {
    data: savedLists,
    sortDir: SORT_DIRECTIONS.DESC,
    sortId: SAVED_LIST_COLUMN_IDS.UPDATED_DATE,
    sortType: SORT_TYPE.DATE,
    selector: s => s.account?.savedLists,
    statusSelector: s => s.account?.savedListsStatus
  };
  const { handleSort, state } = useListSorting(initialSortData);
  const { listData } = state;
  const {
    onPageChange,
    resultPerPage,
    totalResultsCount,
    paginate,
    currentPage,
    paginationLabel,
    viewingPaginationLabel
  } = usePagination(pageSize);

  const shouldDisplayFavToggle = useMemo(() => {
    if (!isCatCSR) {
      return showFavToggle;
    }
    return false;
  }, [isCatCSR, showFavToggle]);

  useEffect(() => {
    getManageViewPreferences();
  }, [getManageViewPreferences]);

  useEffect(() => {
    if (firstRender.current && loadingStatus === STATUS.RESOLVED) {
      dispatch(
        loadSavedListDetails(
          {
            ...searchOptions,
            showMyLists,
            showMyFavs
          },
          showMyLists,
          null,
          null,
          null,
          true
        )
      );
    }
  }, [dispatch, loadingStatus]);

  useEffect(() => {
    if (savedLists.length) {
      firstRender.current = false;
    }
  }, [savedLists]);

  useEffect(() => {
    if (listData?.length) {
      paginate(listData);
    }
  }, [paginate, listData]);

  useEffect(() => {
    if (!resetSavedListPagination && currentPage !== FIRST_PAGE) {
      onPageChange(currentPage);
    }
  }, [listData, onPageChange, currentPage, resetSavedListPagination]);

  const getPaginationLabel = <div slot="inpage-label">{paginationLabel}</div>;
  const isSavedListLoaded = savedListsStatus === STATUS.RESOLVED;

  const manageViewandToggleComponent = (
    <>
      {isCSPCustomer && !isDealerUser && (
        <div className="cat-u-display-flex justify-content-md-start justify-content-center mt-md-0 mt-4">
          {isSavedListLoaded && <ManageView />}
        </div>
      )}
      {shouldDisplayFavToggle && (
        <div className=" cat-u-display-flex justify-content-md-start justify-content-center cat-u-margin-left-md mt-md-0 mt-4">
          <CatToggle
            variant="primary"
            size="sm"
            onBlToggleChange={e => {
              firstRender.current = true;
              saveManageViewPreferences({
                [SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_FAVS]: e.target.checked,
                [SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_LISTS]: !e.target.checked
              });
              dispatch(loadSavedListDetails({ showMyFavs: e.target.checked }));
            }}
            checked={showMyFavs}
            hideLabel
          />
          <span
            className={`cat-u-margin-left-sm text-capitalize ${styles['savedList_Fav']}`}
          >
            {t('SHOW_ONLY_MY_FAVORITES')}
          </span>
        </div>
      )}
    </>
  );

  const resultsAdapter = useCallback(() => {
    if (isCatCSR) {
      return resultPerPage.map(item => {
        const expiresDate = addDays(new Date(item.updated), 5);
        item.expires = format(expiresDate, 'MM/dd/yy');
        return item;
      });
    }

    return resultPerPage;
  }, [resultPerPage, isCatCSR]);

  const savedListTable = !!listData?.length ? (
    <>
      <div
        className={cn(
          `cat-u-display-flex flex-column flex-md-row justify-content-center`,
          {
            'justify-content-md-between':
              (isCSPCustomer || (!isCSPCustomer && isInstantAccess)) &&
              !isCatCSR
          },
          {
            'justify-content-md-end': isCatCSR
          }
        )}
      >
        {manageViewandToggleComponent}
        <CatPagination
          totalRecords={totalResultsCount}
          pageSize={pageSize}
          variant={isMD ? 'in-page' : ''}
          onBlPageChange={e => onPageChange(e.detail.pageNumber)}
          currentItem={currentPage}
        >
          {isMD && getPaginationLabel}
        </CatPagination>
      </div>
      <SavedListTable
        list={resultsAdapter()}
        handleSort={handleSort}
        onPageChange={onPageChange}
      />
      <div className="cat-u-margin-md cat-u-margin-bottom-none justify-content-between">
        <CatPagination
          totalRecords={totalResultsCount}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onBlPageChange={e => {
            setPageSize(e.detail.pageSize);
            onPageChange(e.detail.pageNumber);
          }}
          currentItem={currentPage}
          pageSizeLabel={t('STORE_LIST_SHOW')}
        >
          {<div slot="label">{viewingPaginationLabel}</div>}
        </CatPagination>
      </div>
    </>
  ) : (
    <>
      <div className="mt-4">{manageViewandToggleComponent}</div>

      <SavedListTableEmptyState openCreateListModal={openCreateListModal} />
    </>
  );
  return (
    <>
      {(isLoading && firstRender.current) || listPreferenceLoading ? (
        <LargePaginationLoader />
      ) : (
        savedListTable
      )}
    </>
  );
};

SavedListTableContainer.propTypes = {
  openCreateListModal: PropTypes.func
};

export default SavedListTableContainer;
